/* eslint-disable */
import Vue from 'vue';
import Vuex from 'vuex';
import createLogger from 'vuex/dist/logger';
import {
  SEQUENCE,
  SUBCOMPONENTS,
  COMPONENTS,
} from './scripts/constants';
import _ from 'lodash';
import ClassQuote from './scripts/class-quote';
import CompQuote from './scripts/comp-quote';

const { round } = require('./scripts/utils');

Vue.use(Vuex);

const debug = process.env.NODE_ENV !== 'production';
const dateOptions = { year: 'numeric', month: 'long', day: 'numeric' };
const componentData = {
  components: { ...COMPONENTS },
  subComponents: { ...SUBCOMPONENTS },
};

// Simulates backend work
export default new Vuex.Store({
  state: {
    version: '0.5',
    sequence: SEQUENCE,
    loading: true,
    initialQuarterSelection: false,
    states: {},
    state: "WI",
    calculating: true,
    milkProduction: 1000000,
    quarter: null,
    effectiveDate: new Date().toLocaleString('en-US', dateOptions),
    quoteType: 'blended',
    classWeightingFactor: '1',
    compWeightingFactor: '1',
    classIns: {
      classPercent: 0.0,
      protectionFactor: 1,
      III: {
        price: 15.86,
      },
      IV: {
        price: 14.36,
      },
    },
    compIns: {
      protectionFactor: 1.0,
      proteinTest: 3.00,
      butterFatTest: 3.50,
    },
    componentData: componentData,
    insuranceBlend: 0.0,
    coverageLevel: 95.0,
    simYieldAdjustmentFactor: [],
    expectedYield: 0, // TODO: Make dynamic from 832
    declaredShare: 1,
    expectedPrice: {
      classIII: { avg: 1 },
      classIV: { avg: 1 },
      butterfat:  { avg: 1 },
      protein: { avg: 1 },
      otherSolids: { avg: 1 },
      nonfatSolids: { avg: 1 },
    },
    simPrices: {
      classIII: [],
      classIV: [],
    },
    loadingFactor: 1.0638, // Make dynamic from 833
    availability: {
      class: false,
      componentClassIII: false,
      componentClassIV: false,
    },
    classQuote: ClassQuote,
    compQuote: CompQuote,
    queryLoaded: false,
  },

  getters: {
    quote({
      calculating
    }, {
      revGuarantee,
      liability,
      subsidyAmount,
      totalPremiumAmount,
      producerPremiumAmount
    }) {
      if (calculating) return 0;
      return {
        revGuarantee,
        liability,
        subsidyAmount,
        totalPremiumAmount,
        producerPremiumAmount,
      };
    },

    liability({ insuranceBlend }, { classQuote, compQuote }) {
      const classLiability = insuranceBlend !== 100 ?
        classQuote.liability * ((100 - insuranceBlend) / 100)
        : 0;
      const compLiability = insuranceBlend !== 0 ?
        compQuote.liability * (insuranceBlend / 100)
        : 0;
      const result = classLiability + compLiability;
      return result;
    },

    revGuarantee({ insuranceBlend }, { classQuote, compQuote }) {
      const classRevGuarantee = insuranceBlend !== 100 ?
        classQuote.expRevGuarantee * ((100 - insuranceBlend) / 100)
        : 0;
      const compRevGuarantee = insuranceBlend !== 0 ?
        compQuote.expRevGuarantee * (insuranceBlend / 100)
        : 0;

      const result = classRevGuarantee + compRevGuarantee;
      return result;
    },

    subsidyAmount({ insuranceBlend }, { classQuote, compQuote }) {
      const classSubAmount = insuranceBlend !== 100 ?
        classQuote.subsidyAmount * ((100 - insuranceBlend) / 100)
      : 0;
      const compSubAmount = insuranceBlend !== 0 ?
        compQuote.subsidyAmount * (insuranceBlend / 100)
        : 0;
      return classSubAmount + compSubAmount;
    },

    // Labeled "Premium Before Subsidy" on the FE
    totalPremiumAmount({ insuranceBlend }, { classQuote, compQuote }) {
      const classTotalPremium = insuranceBlend !== 100 ?
        classQuote.totalPremiumAmount * ((100 - insuranceBlend) / 100)
        : 0;
      const compTotalPremium = insuranceBlend !== 0 ?
        compQuote.totalPremiumAmount * (insuranceBlend / 100)
        : 0;

      return classTotalPremium + compTotalPremium;
    },

    producerPremiumAmount({ insuranceBlend }, { classQuote, compQuote } ) {
      const classProducerPremium = insuranceBlend !== 100 ?
        classQuote.producerPremiumAmount * ((100 - insuranceBlend) / 100)
        : 0;
      const compProducerPremium = insuranceBlend !== 0 ?
        compQuote.producerPremiumAmount * (insuranceBlend / 100)
        : 0;

      return classProducerPremium + compProducerPremium;
    },

    subsidyPercent({coverageLevel}) {
      const values = {
        80: 0.55,
        85: 0.49,
        90: 0.44,
        95: 0.44,
      };
      return values[coverageLevel];
    },

    classQuote({
      classIns: {
        protectionFactor,
      },
      coverageLevel,
      declaredShare,
      expectedPrice,
      loadingFactor,
      milkProduction,
      simPrices,
      simYieldAdjustmentFactor,
      calculating,
      classWeightingFactor,
    }, { subsidyPercent }) {
      const zeroQuote = {
        expRevGuarantee: 0,
        totalPremiumAmount: 0,
        liability: 0,
        subsidyAmount: 0,
        producerPremiumAmount: 0,
      };

      if (calculating) {
        return zeroQuote;
      } else {
        console.log('Updating Class Quote...');
      }

      const cpwf = parseFloat(classWeightingFactor);
      const expClassIIIPrice = round(expectedPrice.classIII.avg * cpwf);
      const expClassIVPrice = round(expectedPrice.classIV.avg * (1 - cpwf));
      const expTotalClass = ((expClassIIIPrice + expClassIVPrice) * milkProduction);
      const expRevAmount = Math.ceil(expTotalClass / 100) || 0;
      const expRevGuarantee = Math.ceil((expRevAmount * coverageLevel) / 100) || 0;

      const calcSimRevAmounts = () => {
        const simRevenueAmounts = [];

        SEQUENCE.forEach((idx) => {
          const classIIIPrice = round(simPrices.classIII[idx] * cpwf);
          const classIVPrice = round(simPrices.classIV[idx] * (1 - cpwf));
          const combinedPrice = round(classIIIPrice + classIVPrice);
          const adjustmentFactor = (round(milkProduction * simYieldAdjustmentFactor[idx])) / 100.00;

          simRevenueAmounts[idx] = Math.ceil(combinedPrice * adjustmentFactor);
        });

        return simRevenueAmounts;
      };

      const simRevAmounts = calcSimRevAmounts();
      const simLoss = simRevAmounts.map((value) => round(Math.max(expRevGuarantee - value, 0), 2));
      const calcSimLossAverage = () => {
        const sum = simLoss.reduce((a, b) => a + b, 0);
        const average = sum / 5000;
        const baseline = (0.02 * milkProduction) / 100;
        return round(Math.max(average, baseline), 2);
      };
      const simLossAverage = calcSimLossAverage();
      const preliminaryTotalPremium = Math.ceil(simLossAverage * declaredShare * protectionFactor);
      const totalPremiumAmount = Math.ceil(preliminaryTotalPremium * loadingFactor) || 0;
      const liability = expRevGuarantee * declaredShare * protectionFactor || 0;
      const subsidyAmount = Math.ceil(totalPremiumAmount * subsidyPercent) || 0;
      const producerPremiumAmount = Math.max(Math.ceil(totalPremiumAmount - subsidyAmount), 1) || 0;

      return {
        expRevGuarantee,
        totalPremiumAmount,
        liability,
        subsidyAmount,
        producerPremiumAmount,
      };
    },

    compQuote({
      availability: {
        componentClassIII,
        componentClassIV,
      },
      coverageLevel,
      declaredShare,
      simPrices,
      compIns,
      loadingFactor,
      expectedPrice,
      milkProduction,
      simYieldAdjustmentFactor,
      calculating,
      compWeightingFactor,
    }, { subsidyPercent }) {

      const zeroQuote = {
        expRevGuarantee: 0,
        liability: 0,
        totalPremiumAmount: 0,
        subsidyAmount: 0,
        producerPremiumAmount: 0,
      };

      if (calculating) {
        return zeroQuote;
      } else {
        console.log('Updating Comp Quote...');
      }

      const {
        butterFatTest,
        proteinTest,
      } = compIns;

      // Eject and return all 0s if the quote is 100% for a component class
      // that is unavailable

      if (
        (parseFloat(compWeightingFactor) == 0 && componentClassIV ||
        parseFloat(compWeightingFactor) == 1 && componentClassIII) &&
        (!componentClassIV && !componentClassIII)
      ) {
        console.log('Component weighting selection conflicts with availability.');
        return zeroQuote;
      }

      const butterFatSimPrices = _.get(simPrices, `butterFat`, null);
      const proteinSimPrices = _.get(simPrices, `protein`, null);
      const otherSolidsSimPrices = _.get(simPrices, `otherSolids`, null);
      const nonfatSolidsSimPrices = _.get(simPrices, `nonfatSolids`, null);
      if (!butterFatSimPrices || !proteinSimPrices || !otherSolidsSimPrices) {
        console.error('Missing Prices');
        return zeroQuote;
      }

      const calcSimRevAmounts = () => {
        const cpwf = parseFloat(compWeightingFactor);

        const result = SEQUENCE.map((seq) => {
          const butterFatSimPrice = _.get(simPrices, `butterFat[${seq}]`, null);
          const proteinSimPrice = _.get(simPrices, `protein[${seq}]`, null);
          const otherSolidsSimPrice = _.get(simPrices, `otherSolids[${seq}]`, null);
          const nonfatSolidsSimPrice = _.get(simPrices, `nonfatSolids[${seq}]`, null);

          if (!butterFatSimPrice === null || !proteinSimPrice === null || !otherSolidsSimPrice === null) {
            return console.log(`Missing some data: \n
              butterFat: ${butterFatSimPrice}, \n
              proteinSimPrice: ${proteinSimPrice}\n
              otherSolidsSimPrice: ${otherSolidsSimPrice}
            `);
          }

          return round(
            (
              round(
                cpwf *
                (
                  round(butterFatSimPrice * butterFatTest) +
                  round(proteinSimPrice * proteinTest) +
                  round(otherSolidsSimPrice * 5.7)
                )
              ) +
              round(
                (1 - cpwf) *
                (
                  round(butterFatSimPrice * butterFatTest) +
                  round(nonfatSolidsSimPrice * (proteinTest + 5.7))
                )
              )
            ) *
            (milkProduction * simYieldAdjustmentFactor[seq] / 100.00)
          , 0);
        });

        return result;
      };

      const calcExpRevAmount = () => {
        const cpwf = parseFloat(compWeightingFactor);
        const {
          butterfat: butterFatExpPrice,
          protein: proteinFatExpPrice,
          otherSolids: otherSolidsExpPrice,
          nonfatSolids: nonfatSolidsExpPrice,
        } = expectedPrice;

        if (!nonfatSolidsExpPrice.hasOwnProperty('avg')) nonfatSolidsExpPrice.avg = 0;
        const result = round((round(cpwf * (round(butterFatExpPrice.avg * butterFatTest)
          + round(proteinFatExpPrice.avg * proteinTest) + round(otherSolidsExpPrice.avg * 5.7)))
          + round((1 - cpwf) * (round(butterFatExpPrice.avg * butterFatTest)
          + round(nonfatSolidsExpPrice.avg * (proteinTest + 5.7))))) * (milkProduction / 100.00));

        return round(result, 0);
      };

      const simRevAmounts = calcSimRevAmounts();
      const expRevAmount = calcExpRevAmount();
      const expRevGuarantee = Math.ceil((expRevAmount * coverageLevel) / 100) || 0;
      const simulatedLoss = simRevAmounts.map((value) => round(Math.max(expRevGuarantee - value, 0), 2));
      const sum = simulatedLoss.reduce((a, b) => a + b, 0);
      const simulatedLossAverage = round(Math.max((sum / 5000.00), (0.02 * milkProduction / 100.00)), 2);
      const preliminaryTotalPremium = Math.ceil(simulatedLossAverage * declaredShare * compIns.protectionFactor) || 0;
      const totalPremiumAmount = Math.ceil(preliminaryTotalPremium * loadingFactor) || 0;
      const liability = expRevGuarantee * declaredShare * compIns.protectionFactor || 0;
      const subsidyAmount = Math.ceil(totalPremiumAmount * subsidyPercent) || 0;
      const producerPremiumAmount = Math.max(Math.ceil(totalPremiumAmount - subsidyAmount), 1) || 0;

      return {
        expRevGuarantee,
        liability,
        totalPremiumAmount,
        subsidyAmount,
        producerPremiumAmount
      };
    },

    classDisabled(state) {
      return (
        !state.availability.class ||
        state.insuranceBlend === 100 ||
        state.loading
      );
    },

    componentDisabled(state) {
      const { componentClassIII, componentClassIV } = state.availability;
      return (
        (!componentClassIII && !componentClassIV) ||
        state.insuranceBlend === 0 ||
        state.loading
      );
    },
    protein(state) {
      return {
        prop: 'compIns',
        key: 'proteinTest',
        componentName: 'protein',
        model: 'percent',
        type: 'value',
        minLabel: '$2.75',
        minValue: 2.75,
        minPrice: 2.25,
        maxLabel: '$4.50',
        maxValue: 4.5,
        maxPrice: 4.5,
        value: state.compIns.proteinTest,
        price: 2.25,
        displayValueType: 'percent',
        valueDecimals: 2,
      };
    },
    butterFat(state) {
      return {
        prop: 'compIns',
        key: 'butterFatTest',
        componentName: 'butterFat',
        model: 'percent',
        type: 'value',
        minLabel: 'Butterfat %',
        minValue: 3.25,
        minPrice: 2.25,
        maxLabel: '$5.50',
        maxValue: 5.5,
        maxPrice: 4.5,
        value: state.compIns.butterFatTest,
        price: 2.25,
        displayValueType: 'percent',
        valueDecimals: 2,
      };
    },
  },

  actions: {
    updateProtectionFactorComponentAndQuote({ commit, dispatch, getters }, { value }) {
      commit('updateProtectionFactorComponent', value);
    },
    updateQuote({}, { value })  {

    },
    updateQuarterData({ commit, dispatch, state, getters }, {
      effectiveDate,
      simPrices,
      expectedPrice,
      yieldAdjustmentFactor,
      queryLoaded
    }) {
      commit('updateCalculating', true);
      commit('updateEffectiveDate', effectiveDate);
      commit('updateSimPrices', simPrices);
      commit('updateExpPrice', expectedPrice);
      commit('updateSimulatedYieldAdjustmentFactor', yieldAdjustmentFactor);
      if (queryLoaded === false) {
        commit('updateQueryLoaded', true);
      }
      commit('updateCalculating', false);
    }
  },

  mutations: {
    updateInitialQuarterSelection(state, value) {
      state.initialQuarterSelection = value;
    },
    updateLoading(state, value) {
      state.loading = value;
    },
    updateState(state, value) {
      state.state = value;
    },
    updateMilkProduction(state, value) {
      state.milkProduction = Number(value);
    },
    updateQuarter(state, value) {
      state.quarter = value;
    },
    updateQuoteType(state, value) {
      state.quoteType = value;
    },
    updateClassPercent(state, value) {
      // Stored as decimal string representing a percentage, e.g. '0.4'
      state.classWeightingFactor = (1 - (value / 100)).toString();
    },
    updateComponentPercent(state, value) {
      // Stored as decimal string representing a percentage, e.g. '0.4'
      state.compWeightingFactor = (1 - (value / 100)).toString();
    },
    updateComponent(state, [componentName, key, value]) {
      if (!state.component[componentName]) return;
      state.component[componentName][key] = value;
    },
    updateCoverageLevel(state, value) {
      state.coverageLevel = value;
    },
    updateStates(state, value) {
      state.states = value;
    },
    updateSimPrices(state, value) {
      state.simPrices = value;
    },
    updateSimulatedYieldAdjustmentFactor(state, value) {
      state.simYieldAdjustmentFactor = value;
    },
    updateExpPrice(state, value) {
      state.expectedPrice = value;
    },
    updateLoadingFactor(state, value) {
      state.loadingFactor = value;
    },
    updateProtectionFactorClass(state, value) {
      if (state.insuranceBlend < 100) state.loading = true;
      state.classIns.protectionFactor = parseFloat(value.value);
    },
    updateProtectionFactorComponent(state, value) {
      if (state.insuranceBlend > 0) state.loading = true;
      state.compIns.protectionFactor = parseFloat(value.value);
    },
    updateProperty(state, { prop, key, value }) {
      if (key) {
        state[prop][key] = value;
      } else {
        state[prop] = value;
      }
    },
    updateInsuranceBlend(state, value) {
      state.insuranceBlend = value;
    },

    updateInsuranceOfferId(state, value) {
      state.insuranceOfferID = value;
    },

    updateQueryLoaded(state, value) {
      state.queryLoaded = value;
    },

    updateProteinTest(state, { value }) {
      state.compIns.proteinTest = parseFloat(value);
    },

    updateButterfatTest(state, { value }) {
      state.compIns.butterFatTest = parseFloat(value);
    },

    updateAvailability(state, value) {
      state.availability = value;
    },

    updateEffectiveDate(state, value) {
      state.effectiveDate = value.toLocaleString('en-US', dateOptions);
    },
    updateCalculating(state, value) {
      state.calculating = value;
    },
  },

  strict: debug,
  plugins: debug ? [createLogger()] : [],
});
